// Full working AddProductForm with original structure preserved
// External CSS support assumed — no style or layout changes made

import React, { useState, useEffect } from 'react';
import { FaImage, FaTimes, FaPlus } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';
import RichTextEditor from '../../components/RichTextEditor';
import NumericInput from '../../components/NumericInput';
import { isRichTextEmpty, normalizeDescriptionHtml } from '../../utils/richText';

export default function AddProductForm() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const [itemDetails, setItemDetails] = useState([]);
    const [detailInput, setDetailInput] = useState('');
    const [status, setStatus] = useState('active');
    const [selectedSize, setSelectedSize] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        sizes: [],
        categoryId: ''
    });

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch("https://api.alburaqscents.com/v1/category");
                if (!response.ok) throw new Error('Failed to fetch categories');
                const result = await response.json();
                if (result.success) {
                    setCategories(result.categories);
                } else {
                    throw new Error('API returned unsuccessful response');
                }
            } catch (err) {
                setError(err.message);
                toast.error('Failed to load categories');
            } finally {
                setLoading(false);
            }
        };
        fetchCategories();
    }, []);

    const handleImageUpload = (e) => {
        const files = e.target.files;
        if (selectedImages.length + files.length > 5) {
            toast.error('You can only upload up to 5 images');
            return;
        }
        const newImages = Array.from(files).map(file => ({ file, preview: URL.createObjectURL(file) }));
        setSelectedImages([...selectedImages, ...newImages]);
    };

    const removeImage = (index) => {
        const newImages = [...selectedImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        setSelectedImages(newImages);
    };

    const handleDetailAdd = () => {
        if (detailInput.trim() && !itemDetails.includes(detailInput.trim())) {
            setItemDetails([...itemDetails, detailInput.trim()]);
            setDetailInput('');
        } else {
            toast.error('Detail cannot be empty or duplicate');
        }
    };

    const removeDetail = (index) => {
        const newDetails = [...itemDetails];
        newDetails.splice(index, 1);
        setItemDetails(newDetails);
    };

    const handleCategoryChange = (categoryId) => {
        setFormData({ ...formData, categoryId });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleDescriptionChange = (html) => {
        setFormData((prev) => ({ ...prev, description: html }));
    };

    const addSize = () => {
        const mlValue = parseInt(selectedSize);
        if (mlValue && !formData.sizes.some(size => size.ml === mlValue)) {
            setFormData({
                ...formData,
                sizes: [...formData.sizes, { ml: mlValue, price: '', discount: '', stock: '', skuCode: '' }]
            });
            setSelectedSize('');
        } else {
            toast.error('Please enter a valid size or this size already exists');
        }
    };

    const removeSize = (index) => {
        const newSizes = [...formData.sizes];
        newSizes.splice(index, 1);
        setFormData({ ...formData, sizes: newSizes });
    };

    const isSizeNumberEmpty = (value) => value === '' || value === null || value === undefined;

    const handleSizeChange = (index, field, value) => {
        const newSizes = [...formData.sizes];
        newSizes[index][field] = value;
        setFormData({ ...formData, sizes: newSizes });
    };

    const sizeNumbersComplete =
        formData.sizes.length > 0 &&
        formData.sizes.every(
            (size) =>
                !isSizeNumberEmpty(size.price) &&
                !isSizeNumberEmpty(size.discount) &&
                !isSizeNumberEmpty(size.stock)
        );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) return toast.error('Product title is required');
        if (isRichTextEmpty(formData.description)) return toast.error('Product description is required');
        if (!formData.categoryId) return toast.error('Please select a category');
        if (formData.sizes.length === 0) return toast.error('Please add at least one size option');
        if (!sizeNumbersComplete) {
            return toast.error('Please fill price, discount, and stock for every size');
        }
        if (selectedImages.length === 0) return toast.error('Please upload at least one image');

        let validatedSizes;
        try {
            validatedSizes = formData.sizes.map(size => {
                const ml = Number(size.ml);
                const price = Number(size.price);
                const discount = Number(size.discount);
                const stock = Number(size.stock);
                const skuCode = size.skuCode || '';
                if (isNaN(ml) || ml <= 0) throw new Error(`Invalid size: ${ml}ml`);
                if (isNaN(price) || price <= 0) throw new Error(`Invalid price for ${ml}ml`);
                if (isNaN(discount) || discount < 0 || discount > 100) throw new Error(`Invalid discount for ${ml}ml`);
                if (isNaN(stock) || stock < 0) throw new Error(`Invalid stock for ${ml}ml`);
                return { ml, price, discount, stock, skuCode };
            });
        } catch (err) {
            return toast.error(err.message);
        }

        setIsSubmitting(true);
        const toastId = toast.loading('Uploading...');

        try {
            const payload = new FormData();
            payload.append("name", formData.title.trim());
            payload.append("sizes", JSON.stringify(validatedSizes));
            payload.append("description", normalizeDescriptionHtml(formData.description));
            payload.append("itemDetails", JSON.stringify(itemDetails));
            payload.append("categoryId", formData.categoryId);
            payload.append("status", status);
            selectedImages.forEach((img) => payload.append("images", img.file));

            const res = await fetch("https://api.alburaqscents.com/v1/product", {
                method: "POST",
                body: payload,
            });

            const result = await res.json();
            if (result.success) {
                toast.success('Product created successfully', { id: toastId });
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                toast.error(result.message || 'Error uploading product', { id: toastId });
            }
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong', { id: toastId });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Styles (same as before)
    const styles = {
        container: {
            backgroundColor: '#E5D0A5',
            padding: '0',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontFamily: 'Arial, sans-serif',
            boxSizing: 'border-box',
            width: '100%',
            overflowX: 'hidden',
            position: 'relative',
        },
        formTitle: {
            color: '#000',
            fontSize: '15px',
            fontWeight: 'bold',
            marginBottom: '30px',
            width: '100%',
            textAlign: 'left',
            boxSizing: 'border-box',
            padding: '40px 40px 0 40px',
        },
        formWrapper: {
            backgroundColor: 'transparent',
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
            gap: '30px',
            boxSizing: 'border-box',
            padding: '0 40px 40px 40px',
            '@media (max-width: 800px)': {
                gridTemplateColumns: '1fr',
                padding: '0 20px 20px 20px',
            },
        },
        sectionBox: {
            backgroundColor: '#000',
            borderRadius: '10px',
            padding: '30px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            height: 'fit-content',
            width: '100%',
        },
        sectionTitle: {
            color: '#E5D0A5',
            fontSize: '15px',
            fontWeight: 'bold',
            marginBottom: '0px',
            textAlign: 'left',
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        label: {
            color: '#E5D0A5',
            fontSize: '16px',
            marginBottom: '8px',
            textAlign: 'left',
            fontWeight: 'bold',
        },
        input: {
            backgroundColor: 'transparent',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '12px 15px',
            fontSize: '16px',
            outline: 'none',
            boxSizing: 'border-box',
            width: '100%',
        },
        textarea: {
            backgroundColor: 'transparent',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '12px 15px',
            fontSize: '16px',
            outline: 'none',
            boxSizing: 'border-box',
            minHeight: '120px',
            resize: 'vertical',
            width: '100%',
        },
        uploadPhotoContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            border: '2px dashed #E5D0A5',
            borderRadius: '8px',
            padding: '20px',
            boxSizing: 'border-box',
            minHeight: '180px',
            backgroundColor: '#1E1E1E',
            marginBottom: '20px',
        },
        uploadIcon: {
            fontSize: '60px',
            color: '#E5D0A5',
            marginBottom: '15px',
        },
        uploadButton: {
            backgroundColor: 'transparent',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '10px 25px',
            fontSize: '16px',
            cursor: 'pointer',
            outline: 'none',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            whiteSpace: 'nowrap',
            ':hover': {
                backgroundColor: '#D4AF37',
                color: '#000',
            },
        },
        imagePreviewContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            width: '100%',
            marginTop: '15px',
        },
        imagePreview: {
            position: 'relative',
            width: '80px',
            height: '80px',
            borderRadius: '8px',
            overflow: 'hidden',
        },
        previewImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
        removeImageButton: {
            position: 'absolute',
            top: '5px',
            right: '5px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: '10px',
        },
        checkboxGroup: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            textAlign: 'left',
            gap: '10px',
        },
        checkboxLabel: {
            color: '#E5D0A5',
            fontSize: '16px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontWeight: 500
        },
        checkboxInput: {
            marginRight: '10px',
            width: '20px',
            height: '20px',
            accentColor: '#E5D0A5',
            backgroundColor: 'transparent'
        },
        priceSkuQuantityGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '20px',
            width: '100%',
        },
        infoText: {
            color: '#777',
            fontSize: '12px',
            marginTop: '5px',
            textAlign: 'left',
        },
        statusButtonGroup: {
            display: 'flex',
            gap: '10px',
            width: '100%',
            flexWrap: 'wrap',
        },
        statusButton: {
            borderRadius: '8px',
            padding: '10px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            outline: 'none',
            flex: '1 1 auto',
            minWidth: '100px',
            textAlign: 'center',
            border: 'none',
            transition: 'opacity 0.2s ease',
            ':hover': {
                opacity: 0.8,
            },
        },
        activeButton: {
            backgroundColor: '#A6F75A',
            color: '#000',
        },
        inactiveButton: {
            backgroundColor: '#FF4D4D',
            color: '#FFF',
        },
        submitButton: {
            backgroundColor: '#000',
            color: '#E5D0A5',
            border: 'none',
            borderRadius: '10px',
            padding: '15px 40px',
            fontSize: '15px',
            fontWeight: 'bold',
            float: 'right',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            whiteSpace: 'nowrap',
            zIndex: 10,
            ':hover': {
                backgroundColor: '#C0A020',
                color: '#000',
            },
            '@media (max-width: 800px)': {
                position: 'relative',
                bottom: 'auto',
                right: 'auto',
                marginTop: '30px',
                alignSelf: 'flex-end',
                marginLeft: 'auto',
                marginRight: '20px',
            },
        },
        loadingText: {
            color: '#E5D0A5',
            textAlign: 'center',
        },
        errorText: {
            color: '#FF4D4D',
            textAlign: 'center',
        },
        detailInputContainer: {
            display: 'flex',
            gap: '10px',
            width: '100%',
        },
        detailTagContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginTop: '10px',
        },
        detailTag: {
            backgroundColor: '#1E1E1E',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '15px',
            padding: '5px 15px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
        },
        removeTagButton: {
            color: '#E5D0A5',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
        },
        hiddenFileInput: {
            display: 'none',
        },
        sizeRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr) 30px',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '15px',
            width: '100%'
        },
        sizeInput: {
            backgroundColor: 'transparent',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '10px',
            fontSize: '14px',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box'
        },
        removeSizeButton: {
            backgroundColor: 'transparent',
            color: '#FF4D4D',
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        addSizeButton: {
            backgroundColor: 'transparent',
            color: '#E5D0A5',
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '10px',
            fontSize: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '10px',
            width: '100%',
            ':hover': {
                backgroundColor: '#D4AF37',
                color: '#000'
            }
        },
        sizeCard: {
            border: '1px solid #E5D0A5',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '15px'
        },
        sizeCardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px'
        }
    };

    return (
        <div style={styles.container}>
            <Toaster position="top-center" />

            <h2 style={styles.formTitle}>Add Product</h2>

            <div style={styles.formWrapper}>
                {/* Left Column Sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {/* General Information Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>General</h3>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Title</label>
                            <input
                                type="text"
                                style={styles.input}
                                placeholder="Product Title"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Description</label>
                            <RichTextEditor
                                value={formData.description}
                                onChange={handleDescriptionChange}
                                placeholder="Write an attractive product description with headings, bold text, lists, and colors..."
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Price & Quantity Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>Price & Quantity</h3>

                        {/* Size Selection */}
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Add Size Option</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <NumericInput
                                    style={styles.input}
                                    placeholder="Enter size in ml"
                                    value={selectedSize}
                                    onChange={(e) => setSelectedSize(e.target.value)}
                                    min="1"
                                    disabled={isSubmitting}
                                />
                                <button
                                    style={styles.uploadButton}
                                    onClick={addSize}
                                    disabled={!selectedSize || isSubmitting}
                                >
                                    <FaPlus /> Add
                                </button>
                            </div>
                        </div>

                        {/* Added Sizes */}
                        {formData.sizes.map((size, index) => (
                            <div key={index} style={styles.sizeCard}>
                                <div style={styles.sizeCardHeader}>
                                    <span style={{ color: '#E5D0A5', fontWeight: 'bold' }}>
                                        {size.ml} ml
                                    </span>
                                    <button
                                        style={styles.removeSizeButton}
                                        onClick={() => removeSize(index)}
                                        disabled={isSubmitting}
                                    >
                                        <FaTimes />
                                    </button>
                                </div>

                                <div style={styles.priceSkuQuantityGrid}>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Price</label>
                                        <NumericInput
                                            style={styles.input}
                                            placeholder="Price"
                                            value={isSizeNumberEmpty(size.price) ? '' : size.price}
                                            onChange={(e) => handleSizeChange(index, 'price', e.target.value)}
                                            min="0"
                                            step="0.01"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Discount %</label>
                                        <NumericInput
                                            style={styles.input}
                                            placeholder="Discount"
                                            value={isSizeNumberEmpty(size.discount) ? '' : size.discount}
                                            onChange={(e) => handleSizeChange(index, 'discount', e.target.value)}
                                            min="0"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>Stock</label>
                                        <NumericInput
                                            style={styles.input}
                                            placeholder="Quantity"
                                            value={isSizeNumberEmpty(size.stock) ? '' : size.stock}
                                            onChange={(e) => handleSizeChange(index, 'stock', e.target.value)}
                                            min="0"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                    <div style={styles.inputGroup}>
                                        <label style={styles.label}>SKU Code</label>
                                        <input
                                            type="text"
                                            style={styles.input}
                                            placeholder="SKU Code"
                                            value={size.skuCode || ''}
                                            onChange={(e) => handleSizeChange(index, 'skuCode', e.target.value)}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Item Details Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>Item Details</h3>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Add Detail</label>
                            <div style={styles.detailInputContainer}>
                                <input
                                    type="text"
                                    style={styles.input}
                                    placeholder="Add item detail (e.g., 'Handmade')"
                                    value={detailInput}
                                    onChange={(e) => setDetailInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleDetailAdd()}
                                    disabled={isSubmitting}
                                />
                                <button
                                    style={styles.uploadButton}
                                    onClick={handleDetailAdd}
                                    disabled={isSubmitting}
                                >
                                    Add
                                </button>
                            </div>
                            <div style={styles.detailTagContainer}>
                                {itemDetails.map((detail, index) => (
                                    <div key={index} style={styles.detailTag}>
                                        {detail}
                                        <button
                                            style={styles.removeTagButton}
                                            onClick={() => removeDetail(index)}
                                            disabled={isSubmitting}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column Sections */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {/* Upload Photo Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>Upload Photo</h3>
                        <div style={styles.uploadPhotoContainer}>
                            <input
                                type="file"
                                id="image-upload"
                                style={styles.hiddenFileInput}
                                onChange={handleImageUpload}
                                multiple
                                accept="image/*"
                                disabled={isSubmitting}
                            />
                            <label htmlFor="image-upload" style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                                <FaImage style={styles.uploadIcon} />
                                <div style={{
                                    ...styles.uploadButton,
                                    opacity: isSubmitting ? 0.6 : 1,
                                    pointerEvents: isSubmitting ? 'none' : 'auto'
                                }}>
                                    Upload Photo
                                </div>
                            </label>
                            <p style={{ color: '#E5D0A5', fontSize: '12px', marginTop: '10px' }}>
                                {selectedImages.length}/5 images selected
                            </p>
                            {selectedImages.length > 0 && (
                                <div style={styles.imagePreviewContainer}>
                                    {selectedImages.map((image, index) => (
                                        <div key={index} style={styles.imagePreview}>
                                            <img
                                                src={image.preview}
                                                alt={`Preview ${index}`}
                                                style={styles.previewImage}
                                            />
                                            <button
                                                style={styles.removeImageButton}
                                                onClick={() => removeImage(index)}
                                                disabled={isSubmitting}
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Category Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>Product Category</h3>
                        {loading ? (
                            <p style={styles.loadingText}>Loading categories...</p>
                        ) : error ? (
                            <p style={styles.errorText}>Error: {error}</p>
                        ) : (
                            <div style={styles.checkboxGroup}>
                                {categories.map(category => (
                                    <label key={category._id} style={styles.checkboxLabel}>
                                        <input
                                            type="radio"
                                            name="category"
                                            style={styles.checkboxInput}
                                            value={category._id}
                                            checked={formData.categoryId === category._id}
                                            onChange={() => handleCategoryChange(category._id)}
                                            disabled={isSubmitting}
                                        />
                                        {category.name}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Status Section */}
                    <div style={styles.sectionBox}>
                        <h3 style={styles.sectionTitle}>Status</h3>
                        <div style={styles.statusButtonGroup}>
                            <button
                                style={{
                                    ...styles.statusButton,
                                    ...styles.activeButton,
                                    ...(status === 'active' ? { opacity: 1 } : { opacity: 0.6 }),
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => !isSubmitting && setStatus('active')}
                            >
                                Active
                            </button>
                            <button
                                style={{
                                    ...styles.statusButton,
                                    ...styles.inactiveButton,
                                    ...(status === 'inactive' ? { opacity: 1 } : { opacity: 0.6 }),
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => !isSubmitting && setStatus('inactive')}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    <div>
                        <button
                            style={{
                                ...styles.submitButton,
                                opacity: isSubmitting || !sizeNumbersComplete ? 0.7 : 1,
                                cursor: isSubmitting || !sizeNumbersComplete ? 'not-allowed' : 'pointer'
                            }}
                            onClick={handleSubmit}
                            disabled={isSubmitting || !sizeNumbersComplete}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Product'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}